import type {ScrollReachableControl} from "consent/consentClearance";

/**
 * Reads the app scroller for `consent/consentClearance` (ui#1587) — DOM only, no React, no rules.
 *
 * The rule needs one number per control: how far a real gesture could still lift it. That is a
 * LAYOUT question, and jsdom has no layout (every rect is zero there), so this adapter is covered by
 * the mocked e2e battery rather than by a unit test — `e2e/download-viewport-lock.spec.ts` is where
 * its output is asserted against a real browser.
 */

/**
 * Everything a scroll can land on, and therefore everything the bar must not be allowed to keep.
 *
 * `CookieBanner`'s `ScrollClearance` interpolates this SAME constant into its `scroll-margin-bottom`
 * rule. The two are halves of one guarantee — where a scroll stops, and how much room there is to
 * stop in — so a set spelled out twice would let them drift apart silently.
 */
export const INTERACTIVE_SELECTOR = "button, a, input, textarea, select, [tabindex]";

const SCROLLABLE_OVERFLOW = /^(auto|scroll|overlay)$/;

/**
 * Whether this element is one the page keeps for the visitor, rather than one it has taken away —
 * the nav's closed menus stay mounted and laid out, so their links have real rects and would drag
 * the measurement down to the bottom of a panel nobody can see. Those are `visibility: hidden`,
 * which is what this excludes.
 *
 * Opacity is deliberately NOT consulted. It is the entrance animations' channel, so reading it
 * answers "is this control faded in YET" — a question whose answer changes for a few hundred
 * milliseconds after every mount, with no DOM mutation to announce that it did. Measured at
 * 320x568: the landing's last link counted as absent until its fade finished, the clearance settled
 * at 0 before it appeared, and nothing asked again. Ignoring opacity answers where the control will
 * be instead, which is the same answer once the page is still and the right one before that.
 *
 * Clipping is not consulted either, and that is the reading's known bound: a control parked far down
 * inside an `overflow: hidden` box (a carousel track, a collapsed panel) keeps a real rect, and a
 * non-scrollable ancestor lends it no room, so it comes out resting below the viewport and the rule's
 * clamp pins the clearance at the bar's whole strip. That is the value ui#758 applied to every page
 * unconditionally, so the direction is safe — it gives back this page's saving, never its reach.
 */
function isRendered(element: Element): boolean {
	if (typeof element.checkVisibility === "function") {
		return element.checkVisibility({checkVisibilityCSS: true});
	}
	return getComputedStyle(element).visibility !== "hidden";
}

/**
 * Remaining downward scroll available to `node` and everything above it up to `scroller`, or `null`
 * when a fixed ancestor pins it — no amount of scrolling moves a fixed element, so reserving room
 * for one would reserve it forever.
 *
 * Called with a control's PARENT, never the control: a control that scrolls its own content — a
 * textarea computes `overflow-y: auto` in Chrome, as does a scrollable list — would otherwise lend
 * itself hundreds of pixels of room it cannot use. Scrolling a textarea does not move the textarea.
 *
 * Memoized across the whole pass: controls share ancestors, and a page like `/terms` has hundreds of
 * links over the same handful of containers.
 */
function forwardScrollAvailable(
	node: Element,
	scroller: Element,
	cache: Map<Element, number | null>,
): number | null {
	const cached = cache.get(node);
	if (cached !== undefined) {
		return cached;
	}
	const style = getComputedStyle(node);
	let available: number | null;
	if (style.position === "fixed") {
		available = null;
	} else {
		const scrolls = node === scroller || SCROLLABLE_OVERFLOW.test(style.overflowY);
		const own = scrolls ? Math.max(0, node.scrollHeight - node.clientHeight - node.scrollTop) : 0;
		const parent = node.parentElement;
		const above = node === scroller || parent === null ? 0 : forwardScrollAvailable(parent, scroller, cache);
		available = above === null ? null : own + above;
	}
	cache.set(node, available);
	return available;
}

/**
 * Every control inside `scroller` a gesture could still move, with the room it has left.
 *
 * `excluded` keeps the banner's own buttons out of the reading: they ride on the bar, so they can
 * never be trapped under it, and counting them would make the bar demand room for itself.
 */
export function scrollReachableControls(
	scroller: HTMLElement,
	excluded: readonly Element[] = [],
): ScrollReachableControl[] {
	const cache = new Map<Element, number | null>();
	const controls: ScrollReachableControl[] = [];
	for (const element of scroller.querySelectorAll(INTERACTIVE_SELECTOR)) {
		if (excluded.some((node) => node.contains(element))) {
			continue;
		}
		const rect = element.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0 || !isRendered(element)) {
			continue;
		}
		const parent = element.parentElement;
		if (parent === null || getComputedStyle(element).position === "fixed") {
			continue;
		}
		const liftable = forwardScrollAvailable(parent, scroller, cache);
		if (liftable !== null) {
			controls.push({bottom: rect.bottom, liftable});
		}
	}
	return controls;
}
